import { HEALTH_COVER_MEDICARE_ORIGINAL } from '../config/InsuranceProviders';
import { StoreStateInternal } from '../store/Store';
import { getReferralPaymentRoute, getScreenAnswers } from './ConditionalNextScreenSelectors';
import {
  hasOptedForSelfPay,
  hasOriginalMedicareAnswer,
  isAuCoverageAudience,
  isExcludedFromMedicareAccess,
  isMedicareOriginal,
  isUsNon65Plus,
} from './MedicareAccess';
import { getPartnerInsurer, hasUsedPartnerInsurerAccess } from './PartnerInsurers';

/** AU users who took either exit on the payer screen instead of naming their insurer. */
function hasSkippedAuCoverage(state: StoreStateInternal): boolean {
  const { coverageSelfPay, coverageNoPrivateInsurance } = state.userData;
  return coverageSelfPay?.[0] === 'Yes' || coverageNoPrivateInsurance?.[0] === 'Yes';
}

function isPartnerInsurerAnswer(state: StoreStateInternal, screenName: string): boolean {
  return getPartnerInsurer(getScreenAnswers(state, screenName)?.[0]) !== undefined;
}

/**
 * Routes the insurance-provider dropdown into the coverage flow. A partner insurer's members are
 * offered no-cost access instead of the coverage check. Other AU users who named their insurer go on
 * to the membership number. In the US, Original Medicare skips the plan-type question straight to the
 * address (the dropdown answer already carries the type); every other US non-65+ user answers the
 * plan-type question. Everyone else, and users who chose "I don't want to use my insurance" (or in AU
 * "I don't have private health insurance"), get `undefined` so the config's existing exit applies.
 */
export function getCoverageEntryRoute(
  state: StoreStateInternal,
  screenName: string
): 'partnerInsurer' | 'medicareOriginal' | 'collectCoverage' | 'collectMemberId' | undefined {
  if (isAuCoverageAudience(state)) {
    if (hasSkippedAuCoverage(state)) return undefined;
    return isPartnerInsurerAnswer(state, screenName) ? 'partnerInsurer' : 'collectMemberId';
  }
  if (!isUsNon65Plus(state) || hasOptedForSelfPay(state)) return undefined;
  if (isPartnerInsurerAnswer(state, screenName)) return 'partnerInsurer';
  const selection = getScreenAnswers(state, screenName)?.[0];
  return selection === HEALTH_COVER_MEDICARE_ORIGINAL ? 'medicareOriginal' : 'collectCoverage';
}

/**
 * Referred-flow variant of {@link getCoverageEntryRoute}: users outside the coverage audience keep
 * their referral route (access code / ASL / PAP / Stripe payment) exactly as the dropdown did before.
 */
export function getReferredCoverageEntryRoute(
  state: StoreStateInternal,
  screenName: string
): ReturnType<typeof getCoverageEntryRoute> | ReturnType<typeof getReferralPaymentRoute> {
  return getCoverageEntryRoute(state, screenName) ?? getReferralPaymentRoute(state);
}

/**
 * Routes the partner-insurer screen in Control: users who took the access go straight to payment
 * (the money-back guarantee means nothing at $0); everyone else continues to the usual payment path.
 */
export function getPartnerInsurerAccessRoute(
  state: StoreStateInternal
): 'partnerAccess' | undefined {
  return hasUsedPartnerInsurerAccess(state.userData) ? 'partnerAccess' : undefined;
}

/**
 * Referred-flow variant of {@link getPartnerInsurerAccessRoute}: users who continue without the
 * access keep their referral route (access code / ASL / PAP / Stripe payment).
 */
export function getReferredPartnerInsurerAccessRoute(
  state: StoreStateInternal
): 'partnerAccess' | ReturnType<typeof getReferralPaymentRoute> {
  return getPartnerInsurerAccessRoute(state) ?? getReferralPaymentRoute(state);
}

/**
 * Routes the address screen. Its state decides ACCESS for Original Medicare, so an Original Medicare
 * user who can't find their address is asked for their state instead; everyone else goes on to the
 * consents, then results.
 */
export function getCoverageAddressRoute(state: StoreStateInternal): 'askState' | undefined {
  return state.userData.coverageAddressNotFound?.[0] === 'Yes' && hasOriginalMedicareAnswer(state)
    ? 'askState'
    : undefined;
}

/**
 * Routes the Control results screen. Original Medicare (dropdown or plan-type answer, not self-pay)
 * enters the ACCESS pathway at the disclosure, as MaintenanceExperience routed under-65 Original
 * Medicare before the coverage flow moved after sign-up; everyone else falls through to payment.
 */
export function getCoverageResultsRoute(state: StoreStateInternal): 'medicareOriginal' | undefined {
  return isMedicareOriginal(state) ? 'medicareOriginal' : undefined;
}

/**
 * Routes the referred results placeholder. Original Medicare users (not clinically excluded) enter
 * the ACCESS pathway at the MBI screen, as the dropdown routed them before the coverage flow existed;
 * everyone else re-resolves their referral/payment route.
 */
export function getReferredCoverageResultsRoute(
  state: StoreStateInternal
): 'medicareOriginal' | ReturnType<typeof getReferralPaymentRoute> {
  if (isMedicareOriginal(state) && !isExcludedFromMedicareAccess(state)) return 'medicareOriginal';
  return getReferralPaymentRoute(state);
}
